import { pubsub } from '@services';
import { c as createFocusTrap } from './focus-trap.esm-CoH9VLSd.js';

class MenuPanes extends HTMLElement {
  #pane;
  #panes = this.querySelectorAll('[aria-hidden]');
  #unsubscriber;
  #internalFocusTrapStack = [];

  connectedCallback() {
    this.addEventListener('click', (event) => {
      // Handle click on a back button
      const backLink = event.target.closest('[data-back-button]');
      if (backLink) {
        this.#handleBackLinkClick(backLink);
        return;
      }

      // Handle click on a parent link
      const parentLink = event.target.closest('a[data-target-id]');
      if (parentLink) {
        this.#handleParentLinkClick(parentLink, event);
      }
    });

    // When the drawer opens, reset to initial state
    pubsub.subscribe('drawer:open', ({ id }) => {
      if (id === 'menu-drawer') {
        this.#resetToInitialState();
      }
    });

    // When the drawer is closed, reset all panes
    this.#unsubscriber = pubsub.subscribe('drawer:close', ({ id }) => {
      id === 'menu-drawer' && this.#resetPanes();
    });
  }

  disconnectedCallback() {
    this.#unsubscriber();
  }

  /**
   * Handle click on a Parent link (menu-link-item with nested links)
   * @param {HTMLElement} element - The parent link element
   * @param {MouseEvent} event - The click event
   */
  #handleParentLinkClick(element, event) {
    event.preventDefault();
    // Find the next sibling that is a menu pane
    let nextSibling = element.nextElementSibling;
    while (nextSibling && !nextSibling.hasAttribute('data-menu-pane')) {
      nextSibling = nextSibling.nextElementSibling;
    }
    this.#pane = nextSibling;
    this.#togglePanes();
  }

  /**
   * Handle click on a Back link
   * @param {HTMLElement} element - The back link element
   */
  #handleBackLinkClick() {
    const lastFocusTrap = this.#internalFocusTrapStack.pop();
    if (lastFocusTrap.active) {
      // The deactivate callback will take care of the rest.
      lastFocusTrap.deactivate();
    }
  }

  /**
   * Open the pane
   */
  #openPane() {
    if (!this.#pane) return;
    this.#pane.ariaHidden = 'false';
    setTimeout(() => {
      const focusTrap = createFocusTrap(this.#pane, {
        allowOutsideClick: true,
        clickOutsideDeactivates: (event) => {
          // Allow clicks on drawer close button and other drawer controls
          const drawerControl = event.target.closest('[aria-controls]');
          return !drawerControl;
        },
        onPostDeactivate: () => {
          this.#closePane();
        },
      });
      focusTrap.activate();
      this.#internalFocusTrapStack.push(focusTrap);
    }, 100);
  }

  /**
   * Close the pane
   */
  #closePane() {
    if (!this.#pane) return;

    // Blur any focused element inside the pane before hiding it
    // This prevents the aria-hidden accessibility violation
    if (this.#pane.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    this.#pane.ariaHidden = 'true';
    const parentPane = this.#pane.parentElement.closest('[aria-hidden]');
    this.#pane = parentPane;
  }

  /**
   * Toggle the panes
   */
  #togglePanes() {
    if (!this.#pane) return;
    this.#pane.ariaHidden === 'true' ? this.#openPane() : this.#closePane();
  }

  /**
   * Reset to initial state when drawer opens
   */
  #resetToInitialState() {
    // Clear focus trap stack
    while (this.#internalFocusTrapStack.length > 0) {
      const trap = this.#internalFocusTrapStack.pop();
      if (trap.active) {
        trap.deactivate({ returnFocus: false });
      }
    }

    // Reset pane reference
    this.#pane = null;

    // Find the level-one pane (should be the first one that starts visible)
    const levelOnePane = this.querySelector('[data-menu-pane-level="one"]');

    // Reset all panes
    this.#panes.forEach((pane) => {
      if (pane === levelOnePane) {
        // Level-one pane should be visible
        pane.ariaHidden = 'false';
      } else {
        // All other panes should be hidden
        // Blur any focused elements before hiding
        if (pane.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        pane.ariaHidden = 'true';
      }
    });
  }

  /**
   * Reset the panes when drawer closes
   */
  #resetPanes() {
    const timeout = window.DEFAULT_ANIMATION_DURATION || 300;
    setTimeout(() => {
      // Blur any focused elements in panes before hiding them
      this.#panes.forEach((pane) => {
        if (pane.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        pane.ariaHidden = 'true';
      });
    }, timeout);
  }
}

customElements.define('menu-panes', MenuPanes);
